import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LinkedinLogo, YoutubeLogo, InstagramLogo, XLogo } from '@phosphor-icons/react';
import { getFooterSettings, updateFooterSettings } from '@/utils/api';

const NETWORKS = [
  { key: 'linkedin', label: 'LinkedIn', Icon: LinkedinLogo, hint: 'https://www.linkedin.com/company/…' },
  { key: 'youtube', label: 'YouTube', Icon: YoutubeLogo, hint: 'https://www.youtube.com/@…' },
  { key: 'instagram', label: 'Instagram', Icon: InstagramLogo, hint: 'https://www.instagram.com/…' },
  { key: 'twitter', label: 'X (Twitter)', Icon: XLogo, hint: 'https://twitter.com/…' },
];

const AdminFooterSettings = () => {
  const [socialLinks, setSocialLinks] = useState({ linkedin: '', youtube: '', instagram: '', twitter: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getFooterSettings()
      .then(r => setSocialLinks({ ...socialLinks, ...(r.data?.social_links || {}) }))
      .catch(() => toast.error('Failed to load footer settings'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await updateFooterSettings({ social_links: socialLinks });
      setSocialLinks(r.data.social_links);
      toast.success('Footer settings saved. Audit log created.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8"><div className="loader"></div></div>;

  return (
    <div className="p-8 space-y-6" data-testid="admin-footer-settings-page">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Footer Settings</h1>
          <p className="text-slate-600 mt-1">Manage the social media links shown in the public footer.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-footer-settings">
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        Links must start with <code>http://</code> or <code>https://</code>. Leave a field empty to hide that icon from the footer.
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-200">
        <h2 className="text-xl font-bold mb-4 text-brand-primary">Social Media Links</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {NETWORKS.map(({ key, label, Icon, hint }) => (
            <div key={key} className="space-y-1">
              <Label className="flex items-center gap-2 text-slate-700">
                <Icon size={20} weight="fill" />
                <span>{label}</span>
              </Label>
              <Input
                type="url"
                placeholder={hint}
                value={socialLinks[key] || ''}
                onChange={(e) => setSocialLinks({ ...socialLinks, [key]: e.target.value })}
                data-testid={`footer-social-${key}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminFooterSettings;
